import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Grid2 as Grid,
  Card,
  CardContent,
  CardActionArea,
  Typography,
  Button,
  CircularProgress,
  Alert,
  Chip,
  Tooltip,
} from '@mui/material';
import { Link as LinkIcon, Public as PublicIcon, Business as BusinessIcon } from '@mui/icons-material';
import pagesService from '../../services/pagesService';
import { useThemeCustom } from '../../hooks/useThemeCustom';

/**
 * PagesEmptyState - Componente de empty state para listagem de páginas
 *
 * Exibe cards grandes com tipos de páginas disponíveis quando não há páginas criadas.
 * Padrão: 2 colunas desktop, 1 coluna mobile.
 * Tipos inativos com "EM BREVE" e opacity: 0.6.
 */
export default function PagesEmptyState() {
  const navigate = useNavigate();
  const [pageTypes, setPageTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { getLabel } = useThemeCustom();

  // Mapeamento de ícones Material-UI por nome
  const iconMap = {
    'Link': LinkIcon,
    'Business': BusinessIcon,
    'Person': PublicIcon,
    'default': LinkIcon
  };

  useEffect(() => {
    loadPageTypes();
  }, []);

  const loadPageTypes = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await pagesService.getPageTypes();

      // Mapeamento de labels dinâmicos baseados no template ativo
      const dynamicLabels = {
        'clinic': {
          name: `Página de ${getLabel('places')}`,
          description: `Página profissional com informações sobre seu ${getLabel('places').toLowerCase()}, especialidades e equipe`
        },
        'professional': {
          name: `Página de ${getLabel('professionals')}`,
          description: `Crie uma página pessoal com sua experiência, especialidades e agenda de ${getLabel('schedules').toLowerCase()}`
        }
      };

      // Transformar resposta da API para formato interno
      const formatted = (data && Array.isArray(data) ? data : (data?.$values || [])).map(pt => {
        // Usar labels dinâmicos se disponíveis para este tipo
        const dynamicLabel = dynamicLabels[pt.type];

        return {
          id: pt.type,
          name: dynamicLabel?.name || pt.name,
          description: dynamicLabel?.description || pt.description || '',
          icon: iconMap[pt.iconName] || iconMap['default'],
          enabled: pt.isActive,
          comingSoon: !pt.isActive,
          color: pt.isActive ? '#1976d2' : '#999',
          linkLabel: pt.linkLabel || (pt.isActive ? 'Criar' : 'EM BREVE'),
          displayOrder: pt.displayOrder,
          type: pt.type,
          beta: pt.type === 'professional',
        };
      }).sort((a, b) => a.displayOrder - b.displayOrder);

      setPageTypes(formatted);
    } catch (err) {
      console.error('Erro ao carregar tipos de página:', err);
      setError('Não foi possível carregar os tipos de página');

      // Fallback: usar tipos estáticos em caso de erro
      setPageTypes([
        {
          id: 'link_in_bio',
          name: 'Link in Bio',
          description: 'Crie uma página personalizável com links para suas redes sociais, agenda e outras informações',
          icon: LinkIcon,
          enabled: true,
          color: '#1976d2',
          linkLabel: 'Criar',
          displayOrder: 1,
          type: 'link_in_bio'
        },
        {
          id: 'clinic',
          name: `Página de ${getLabel('places')}`,
          description: `Página profissional com informações sobre seu ${getLabel('places').toLowerCase()}, especialidades e equipe`,
          icon: BusinessIcon,
          enabled: false,
          comingSoon: true,
          color: '#999',
          linkLabel: 'EM BREVE',
          displayOrder: 2,
          type: 'clinic'
        },
        {
          id: 'professional',
          name: `Página de ${getLabel('professionals')}`,
          description: 'Crie uma página pessoal com sua experiência, especialidades e agenda',
          icon: PublicIcon,
          enabled: false,
          comingSoon: true,
          color: '#999',
          linkLabel: 'EM BREVE',
          displayOrder: 3,
          type: 'professional'
        }
      ]);
    } finally {
      setLoading(false);
    }
  };

  const handleCreatePage = (type) => {
    if (type.enabled) {
      navigate(`/pages/new/${type.id}`);
    }
  };

  if (loading) {
    return (
      <Container maxWidth="md" sx={{ py: 8 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ py: 8 }}>
      {/* Header Section */}
      <Box sx={{ textAlign: 'center', mb: 6 }}>
        <Typography
          variant="h4"
          component="h1"
          sx={{
            fontWeight: 700,
            mb: 2,
            color: 'text.primary'
          }}
        >
          Comece Criando uma Página
        </Typography>
        <Typography
          variant="body1"
          color="textSecondary"
          sx={{
            maxWidth: 500,
            mx: 'auto',
            lineHeight: 1.6
          }}
        >
          Escolha um tipo de página para começar a criar sua presença online personalizada.
        </Typography>
      </Box>

      {/* Error Alert */}
      {error && (
        <Alert
          severity="warning"
          sx={{ mb: 4 }}
          onClose={() => setError(null)}
        >
          {error} — Usando opções padrão
        </Alert>
      )}

      {/* Page Type Cards Grid */}
      <Grid container spacing={3}>
        {pageTypes.map((type) => (
          <Grid size={{ xs: 12, sm: 6 }} key={type.id}>
            <Card
              sx={{
                height: '100%',
                cursor: type.enabled ? 'pointer' : 'not-allowed',
                opacity: type.enabled ? 1 : 0.6,
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                '&:hover': {
                  boxShadow: type.enabled ? 6 : 1,
                  transform: type.enabled ? 'translateY(-8px)' : 'none',
                  bgcolor: type.enabled ? 'action.hover' : 'transparent'
                },
                border: '1px solid',
                borderColor: 'divider'
              }}
            >
              <CardActionArea
                disabled={!type.enabled}
                onClick={() => handleCreatePage(type)}
                sx={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column'
                }}
              >
                <CardContent
                  sx={{
                    textAlign: 'center',
                    py: 5,
                    px: 3,
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'space-between'
                  }}
                >
                  {/* Icon - 72px */}
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'center',
                      mb: 3
                    }}
                  >
                    <type.icon
                      sx={{
                        fontSize: 72,
                        color: type.color,
                        opacity: type.enabled ? 1 : 0.5
                      }}
                      aria-hidden="true"
                    />
                  </Box>

                  {/* Title */}
                  <Box sx={{ mb: 2 }}>
                    <Typography
                      variant="h6"
                      component="h2"
                      sx={{
                        fontWeight: 700,
                        mb: 0.5,
                        color: type.enabled ? 'text.primary' : 'text.secondary',
                        fontSize: '1.25rem'
                      }}
                    >
                      {type.name}
                      {type.beta && (
                        <Chip
                          label="BETA"
                          size="small"
                          color="info"
                          variant="filled"
                          sx={{
                            ml: 1,
                            fontSize: '0.65rem',
                            height: 20,
                            fontWeight: 700,
                            verticalAlign: 'middle'
                          }}
                        />
                      )}
                    </Typography>
                  </Box>

                  {/* Description */}
                  <Typography
                    variant="body2"
                    color="textSecondary"
                    sx={{
                      mb: 3,
                      lineHeight: 1.6,
                      minHeight: '3em',
                      color: type.enabled ? 'text.secondary' : 'text.disabled'
                    }}
                  >
                    {type.description}
                  </Typography>

                  {/* Coming Soon Badge ou CTA Button */}
                  {type.comingSoon ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                      <Tooltip title="Funcionalidade em desenvolvimento — disponível em breve">
                        <span style={{ cursor: 'not-allowed', display: 'inline-block' }}>
                          <Button
                            variant="outlined"
                            disabled
                            sx={{
                              cursor: 'not-allowed',
                              fontWeight: 600,
                              fontSize: '0.85rem'
                            }}
                          >
                            EM BREVE
                          </Button>
                        </span>
                      </Tooltip>
                    </Box>
                  ) : (
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={() => handleCreatePage(type)}
                      sx={{
                        alignSelf: 'center',
                        fontWeight: 600
                      }}
                    >
                      {type.linkLabel}
                    </Button>
                  )}
                </CardContent>
              </CardActionArea>
            </Card>
          </Grid>
        ))}
      </Grid>
    </Container>
  );
}
